import { cx } from "@suse-o11y/class-variance";
import type { Time } from "@suse-o11y/date-time/time";
import { Dialog, DialogClose, useDialogClose } from "@suse-o11y/dialog";
import {
	IconArrowDownRounded,
	IconAudioMutedOutlined,
	IconClose,
	IconLeftOutlined,
	IconSoundOutlined,
} from "@suse-o11y/icons";
import { LocalStorage, MONITOR_STATUS_DRAWER_SCROLL_INDICATOR } from "@suse-o11y/local-storage";
import { Signal, useMonitor } from "@suse-o11y/signal";
import { usePermanent } from "@suse-o11y/use-permanent";
import React, { useState } from "react";
import { z } from "zod";

import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";
import type { NodeId } from "~/models/dto";

import type { TimeRange } from "../../../../../app/state/view/timeline/TimeRange";
import { isHealthStateUnHealthy } from "../../../../../common/functions";
import type { Api, MonitorCheckStatus } from "../../../../../common/openapi";
import { ComponentBoundary } from "../../../../../components/_component-boundary";
import { LinkTo } from "../../../../../components/react/link";
import { LoadingSpinner } from "../../../../../components/react/loading-spinner/LoadingSpinner";
import { NotFound } from "../../../../../components/react/not-found/NotFound";
import { StateComponent } from "../../../../../components/react/states/StateComponent";
import type { SimpleStatefulComponent } from "../../../../../components/react/states/state-component-utils";
import { queryMonitorCheckStatus, useCreateSilencingRule, useDeleteSilencingRule } from "../../../../../queries";
import { useQuery, useQueryClient } from "../../../../../queries/react-query";
import { queryUIConfig } from "../../../../../queries/ui-config";
import { COMPONENT_PATH } from "../../../../../routes/Routes";
import { usePinnedItemsState } from "../../../panes/east/pinned-items";
import type { PinnedItemsState } from "../../../panes/east/pinned-items/PinnedItemsState";
import { PinningButton } from "../../../panes/east/pinned-items/PinningButton";
import { MonitorHealthTimeline } from "../MonitorHealthTimeline";
import { MonitorIntro } from "../MonitorIntro";
import { MonitorRemediationTabs } from "../MonitorRemediationTabs";
import { MonitorStatusDrawerHealthState } from "../MonitorStatusDrawerHealthState";
import { MonitorStatusDrawerTabs } from "../MonitorStatusDrawerTabs";

import { MonitorStatusDrawerProvider } from "./monitor-status-drawer-provider";
import { CheckStateChain } from "./monitor-status-drawer-state";

interface MonitorStatusDrawerProps {
	monitorStatusIdentifier: string;
	timestamp: undefined | Time;
	timeRange: TimeRange;
}

export const MonitorStatusDrawer: React.FC<MonitorStatusDrawerProps> = (props) => (
	<Dialog className="drawer/dialog drawer-wide">
		<MonitorStatusDrawerContent {...props} />
	</Dialog>
);

const MonitorStatusDrawerContent: React.FC<MonitorStatusDrawerProps> = ({
	monitorStatusIdentifier,
	timestamp,
	timeRange,
}) => {
	const monitor = useMonitor();
	const pinnedItemsState = usePinnedItemsState();
	const scrollRef = React.useRef<HTMLDivElement>(null);

	const [state, timestampSignal, timeRangeSignal] = usePermanent(() => [
		CheckStateChain.init({
			identifier: monitorStatusIdentifier,
			selectedTab: Signal(),
			showImpactedComponent: Signal(false),
		}),
		Signal(timestamp ?? Date.now()),
		Signal(timeRange),
	]);

	const checkState = state.peek(monitor);

	const [cachedData, setCachedData] = useState<MonitorCheckStatus>();
	const {
		data = cachedData?.identifier === checkState?.identifier ? cachedData : undefined,
		isFetching,
		error,
	} = useQuery({
		keepPreviousData: cachedData?.identifier === checkState?.identifier,
		enabled: checkState != null,
		onSuccess: (responseData) => setCachedData(responseData),
		...queryMonitorCheckStatus({
			identifier: checkState?.identifier ?? "",
			timestamp: timestampSignal.read(monitor),
		}),
	});

	const component: undefined | SimpleStatefulComponent = data && {
		id: data.component.id as NodeId,
		name: data.component.name,
		identifiers: [data.component.identifier],
		typeName: data.component.type,
		icon: data.component.icon,
		healthState: data.health,
	};

	React.useEffect(() => {
		if (checkState != null) {
			scrollRef.current?.scrollTo(0, 0);
		}
	}, [checkState]);

	const scrollDownIndicatorVisible =
		LocalStorage.getItem(monitor, MONITOR_STATUS_DRAWER_SCROLL_INDICATOR, z.boolean()) ?? true;

	const showScrollIndicator =
		scrollDownIndicatorVisible &&
		scrollRef.current != null &&
		scrollRef.current.clientHeight < scrollRef.current.scrollHeight;

	return (
		<>
			<header className="drawer-header">
				<StateComponent
					className="mr-auto"
					component={component}
					displayName
					displayTypeName
					typeOfData="Monitor status"
				/>

				{state.size(monitor) > 1 && (
					<button className="button button-tertiary gap-2" onClick={() => state.pop()} type="button">
						<IconLeftOutlined className="text-xs" />
						Previous
					</button>
				)}

				{error == null && <PinningButton item={data == null ? undefined : { ...data, timeRange: timeRangeSignal }} />}

				<DialogClose aria-label="Close" className="button button-square">
					<IconClose className="size-3" />
				</DialogClose>
			</header>

			<section
				className="drawer-body"
				onScroll={() => {
					LocalStorage.setItem(MONITOR_STATUS_DRAWER_SCROLL_INDICATOR, false);
				}}
				ref={scrollRef}
			>
				<ComponentBoundary component={data?.component}>
					<MonitorStatusDrawerProvider timeRange={timeRangeSignal} timestamp={timestampSignal}>
						{data && <MonitorIntro monitorStatus={data} />}
						{!isFetching && checkState && (
							<MonitorHealthTimeline
								action={error == null && data != null && <MuteButton data={data} />}
								disabled={isFetching}
								identifier={checkState.identifier}
							/>
						)}

						{isFetching && <LoadingSpinner className="h-full" size="large" />}

						{!isFetching && error != null && (
							<NotFound header="No data found">
								Either the component or the observed health state does not exist at the selected time.
								<br />
								Choose a different time by clicking the monitor health timeline in a non-grey area.
							</NotFound>
						)}

						{!(isFetching || error) && data && (
							<>
								<MonitorStatusDrawerHealthState
									health={data.health}
									reason={data.reason}
									selectedTimestamp={data.topologyTime}
									triggeredTimeStamp={data.triggeredTimestamp}
								/>

								<div>
									<MonitorStatusDrawerTabs checkState={data} checkStateChain={state} />
								</div>

								{isHealthStateUnHealthy(data.health) && (
									<MonitorRemediationSection
										data={data}
										pinnedItemsState={pinnedItemsState}
										timeRange={timeRangeSignal}
									/>
								)}
							</>
						)}

						<ScrollIndicator show={showScrollIndicator} />
					</MonitorStatusDrawerProvider>
				</ComponentBoundary>
			</section>

			<footer className="drawer-footer">
				<DialogClose className="button button-tertiary uppercase">Close</DialogClose>

				<DialogClose asChild>
					<LinkTo
						className="button button-primary aria-disabled:skeleton uppercase"
						disabled={data == null}
						params={{
							identifier: data?.component.identifier ?? "fallback",
						}}
						search={(search) => ({
							...search,
							query: undefined,
							explore: undefined,
						})}
						theme="unstyled"
						to={COMPONENT_PATH}
					>
						View component
					</LinkTo>
				</DialogClose>
			</footer>
		</>
	);
};

const MonitorRemediationSection: React.FC<{
	data: MonitorCheckStatus;
	pinnedItemsState: PinnedItemsState;
	timeRange: Signal<TimeRange>;
}> = ({ data, pinnedItemsState, timeRange }) => {
	const close = useDialogClose();

	if (!data.troubleshootingSteps) {
		return null;
	}

	return (
		<MonitorRemediationTabs
			componentIdentifier={data.component.identifier}
			onLinkClick={(autoPinEnabled) => {
				if (autoPinEnabled) {
					pinnedItemsState.pin({
						...data,
						component: data.component,
						timeRange,
					});
				}
				close();
			}}
			text={data.troubleshootingSteps}
			timestamp={data.topologyTime}
		/>
	);
};

const MuteButton: React.FC<{ data: MonitorCheckStatus }> = ({ data }) => {
	const queryClient = useQueryClient();
	const silencingEnabled = useQuery({
		...queryUIConfig,
		select: (config) => config.webUIConfig.featureFlags.silencing,
	}).data;

	const setSilencingRule = (silencingRule: MonitorCheckStatus["silencingRule"]): void => {
		queryClient.setQueriesData<MonitorCheckStatus | undefined>(
			[{}, "monitor", "checkStatus", data.identifier],
			(current) => (current == null ? current : { ...current, silencingRule }),
		);
	};

	const createSilencingRule = useCreateSilencingRule({
		onSuccess: ({ identifier, note, expiryEpochSeconds }) => {
			setSilencingRule({ identifier, note, expiryEpochSeconds });
		},
	});
	const deleteSilencingRule = useDeleteSilencingRule({
		onSuccess: () => {
			setSilencingRule(undefined);
		},
	});

	if (!silencingEnabled) {
		return null;
	}

	const silencingRule = data.silencingRule;
	if (silencingRule != null) {
		return (
			<button
				className="button button-secondary gap-2 uppercase"
				disabled={deleteSilencingRule.isLoading}
				onClick={() => deleteSilencingRule.mutate(silencingRule.identifier)}
				type="button"
			>
				<IconSoundOutlined />
				Unmute
			</button>
		);
	}

	const selector: Api.MonitorCheckStateSelector = {
		type: "MonitorCheckStateSelector",
		monitorId: data.monitorId,
		checkStateId: data.checkStateId,
		subStream: data.subStream,
	};

	return (
		<button
			className="button button-secondary gap-2 uppercase"
			disabled={createSilencingRule.isLoading}
			onClick={() => createSilencingRule.mutate({ note: "Manually suppressed by the user", selector })}
			type="button"
		>
			<IconAudioMutedOutlined />
			Mute
		</button>
	);
};

const ScrollIndicator: React.FC<{ show: boolean }> = ({ show }) => (
	<Tooltip open={show === false ? false : undefined}>
		<TooltipTrigger
			asChild={false}
			className={cx(
				"absolute inset-x-1/2 bottom-[72px] z-10 flex h-[72px] w-10 -translate-x-1/2 flex-col items-center justify-center gap-3 rounded-[30px] bg-grey-800 text-white transition-opacity duration-500",
				show ? "opacity-100" : "opacity-0",
			)}
		>
			<div className="relative h-[22px] w-4 rounded-lg border border-white">
				<span className="absolute inset-x-1/2 top-[29%] h-1 w-1 -translate-x-1/2 animate-move rounded-full bg-white" />
			</div>
			<IconArrowDownRounded className="text-[8px]" />
		</TooltipTrigger>

		<TooltipContent>Scroll down</TooltipContent>
	</Tooltip>
);
